import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField } from 'v5/design-system/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type {
  FieldFrameSpec,
  FieldPartProps,
  PlaceholderSpec
} from './fieldComponent.types';

/** Select over `string | null`. */
export function SingleSelectField({
  spec,
  path,
  hideLabel
}: FieldPartProps<
  FieldFrameSpec &
    PlaceholderSpec &
    Pick<FieldSpecOf<'single-select'>, 'options' | 'clearable'>
>) {
  const { t } = useTranslation('ds');
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const selected: string = useWatch({ name: path }) ?? '';

  if (readOnly && selected === '') {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field: { onChange, value, ref, onBlur, name } }) => (
        <FieldControlBase spec={spec} hideLabel={hideLabel}>
          <Select
            name={name}
            value={value ?? ''}
            onValueChange={onChange}
            clearable={spec.clearable}
            onClear={() => onChange(null)}
          >
            <SelectTrigger
              variant={ctx.variant}
              size={ctx.size}
              readOnly={readOnly}
              ref={ref}
              onBlur={onBlur}
            >
              <SelectValue
                placeholder={spec.placeholder ?? t('Select.emptyPlaceholder')}
              />
            </SelectTrigger>
            <SelectContent>
              {spec.options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldControlBase>
      )}
    />
  );
}
